import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import BackofficeLayout from "@/components/layouts/BackofficeLayout";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Plus, BarChart3, Activity, Edit, Loader2, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface Dashboard {
  id: number;
  name: string;
  description: string;
  created_at: string;
}

const Analytics = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [dashboards, setDashboards] = useState<Dashboard[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState<string>("");
  const [editingDescription, setEditingDescription] = useState<string>("");

  // Load dashboards from database
  useEffect(() => {
    const loadDashboards = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('analytics_dashboards')
          .select('*')
          .order('created_at', { ascending: false });

        if (error) throw error;

        setDashboards(data || []);
      } catch (err) {
        console.error("Error loading dashboards:", err);
        toast({
          title: "Error",
          description: "Failed to load dashboards",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    loadDashboards();
  }, [toast]);

  const handleCreateDashboard = async () => {
    try {
      setCreating(true);

      // Get current user
      const { data: { user: authUser }, error: authError } = await supabase.auth.getUser();

      if (authError || !authUser) {
        toast({
          title: "Error",
          description: "You must be logged in to create dashboards",
          variant: "destructive",
        });
        return;
      }

      // Get user profile
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('id')
        .eq('auth_user_id', authUser.id)
        .single();

      if (userError || !userData) {
        toast({
          title: "Error",
          description: "Could not find user profile",
          variant: "destructive",
        });
        return;
      }

      // Create new dashboard
      const { data: newDashboard, error: createError } = await supabase
        .from('analytics_dashboards')
        .insert({
          name: 'Untitled Dashboard',
          description: '',
          created_by: userData.id,
        })
        .select()
        .single();

      if (createError) {
        console.error("Dashboard creation error:", createError);
        toast({
          title: "Error",
          description: `Failed to create dashboard: ${createError.message}`,
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Success",
        description: "Dashboard created successfully",
      });

      // Navigate to edit the new dashboard
      navigate(`/backoffice/analytics/${newDashboard.id}/edit`);
    } catch (error) {
      console.error("Unexpected error creating dashboard:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteDashboard = async (dashboardId: number) => {
    try {
      const { error } = await supabase
        .from('analytics_dashboards')
        .delete()
        .eq('id', dashboardId);

      if (error) {
        console.error("Dashboard deletion error:", error);
        toast({
          title: "Error",
          description: `Failed to delete dashboard: ${error.message}`,
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Success",
        description: "Dashboard deleted successfully",
      });

      // Refresh the list
      setDashboards(dashboards.filter(d => d.id !== dashboardId));
    } catch (error) {
      console.error("Unexpected error deleting dashboard:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive",
      });
    }
  };

  const startEditing = (dashboard: Dashboard) => {
    setEditingId(dashboard.id);
    setEditingName(dashboard.name);
    setEditingDescription(dashboard.description);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditingName("");
    setEditingDescription("");
  };

  const saveEditing = async (dashboardId: number) => {
    try {
      const { error } = await supabase
        .from('analytics_dashboards')
        .update({
          name: editingName,
          description: editingDescription,
        })
        .eq('id', dashboardId);

      if (error) {
        console.error("Dashboard update error:", error);
        toast({
          title: "Error",
          description: `Failed to update dashboard: ${error.message}`,
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Success",
        description: "Dashboard updated successfully",
      });

      // Update the local state
      setDashboards(dashboards.map(d =>
        d.id === dashboardId
          ? { ...d, name: editingName, description: editingDescription }
          : d
      ));

      cancelEditing();
    } catch (error) {
      console.error("Unexpected error updating dashboard:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive",
      });
    }
  };

  return (
    <BackofficeLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Analytics</h1>
            <p className="text-muted-foreground">
              View and manage analytics dashboards
            </p>
          </div>
          <Button onClick={handleCreateDashboard} disabled={creating}>
            {creating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Plus className="mr-2 h-4 w-4" />
                Create Dashboard
              </>
            )}
          </Button>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <p className="ml-4 text-muted-foreground">Loading dashboards...</p>
          </div>
        ) : dashboards.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <BarChart3 className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No dashboards yet</h3>
              <p className="text-muted-foreground mb-4 text-center max-w-sm">
                Create your first analytics dashboard to start tracking your
                business metrics
              </p>
              <Button onClick={handleCreateDashboard} disabled={creating}>
                {creating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus className="mr-2 h-4 w-4" />
                    Create Your First Dashboard
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="px-4 py-3 text-left text-sm font-medium">Name</th>
                      <th className="px-4 py-3 text-left text-sm font-medium">Description</th>
                      <th className="px-4 py-3 text-left text-sm font-medium">Created</th>
                      <th className="px-4 py-3 text-right text-sm font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboards.map((dashboard) => (
                      <tr key={dashboard.id} className="border-b last:border-b-0 hover:bg-muted/30">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <BarChart3 className="h-4 w-4 text-muted-foreground" />
                            {editingId === dashboard.id ? (
                              <input
                                type="text"
                                value={editingName}
                                onChange={(e) => setEditingName(e.target.value)}
                                onBlur={() => saveEditing(dashboard.id)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    saveEditing(dashboard.id);
                                  } else if (e.key === 'Escape') {
                                    cancelEditing();
                                  }
                                }}
                                className="font-medium px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                autoFocus
                              />
                            ) : (
                              <span
                                className="font-medium cursor-pointer hover:text-blue-600"
                                onClick={() => startEditing(dashboard)}
                              >
                                {dashboard.name}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">
                          {editingId === dashboard.id ? (
                            <input
                              type="text"
                              value={editingDescription}
                              onChange={(e) => setEditingDescription(e.target.value)}
                              onBlur={() => saveEditing(dashboard.id)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  saveEditing(dashboard.id);
                                } else if (e.key === 'Escape') {
                                  cancelEditing();
                                }
                              }}
                              className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="Description"
                            />
                          ) : (
                            <span
                              className="cursor-pointer hover:text-blue-600"
                              onClick={() => startEditing(dashboard)}
                            >
                              {dashboard.description || '-'}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">
                          {new Date(dashboard.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => navigate(`/backoffice/analytics/${dashboard.id}`)}
                            >
                              <Activity className="h-4 w-4 mr-1" />
                              View
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => navigate(`/backoffice/analytics/${dashboard.id}/edit`)}
                            >
                              <Edit className="h-4 w-4 mr-1" />
                              Edit
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button size="sm" variant="outline">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete Dashboard</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Are you sure you want to delete "{dashboard.name}"? This action cannot be undone and will also delete all widgets in this dashboard.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleDeleteDashboard(dashboard.id)}
                                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                  >
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </BackofficeLayout>
  );
};

export default Analytics;
